// Shared pieces of the V2 test screens (BRIEFS C7.4): question html, option rows with their
// right/wrong labels, the "Read this in your notes" box, the open-test banner, a simple
// confirm dialog and the link into the notes.
import React from 'react';
import { Link } from 'react-router-dom';
import MathHtml from '../shared/MathHtml';
import { IconArrowRight, IconBook, IconOpen, IconRight, IconWrong } from './icons';
import { C, FONT, button } from './theme';

export const LETTERS = ['A', 'B', 'C', 'D'];

// Question text from the old bank: html, or plain text with line breaks.
function toHtml(s) {
  const t = s || '';
  return t.indexOf('<') >= 0 ? t : t.split('\n').join('<br>');
}
export function QHtml({ html, style, tag = 'div' }) {
  return <MathHtml tag={tag} style={style} html={toHtml(html)} />;
}

// Notes colours inside a point's html (same classes as the Notes screen, C4.3).
export const POINT_CSS = `
.v2-qpt .c-red{color:${C.noteRed};font-weight:700}
.v2-qpt .c-blue{color:${C.noteBlue};font-weight:700}
.v2-qpt .c-green{color:${C.noteGreen};font-weight:700}
.v2-qpt .c-mark{background:${C.marker}}
.v2-qpt table{border-collapse:collapse;margin:6px 0;font-size:14px;line-height:1.35}
.v2-qpt th,.v2-qpt td{border:1px solid #CBD5E1;padding:4px 8px;text-align:left;vertical-align:top}
.v2-qpt th{background:#F1F5F9;font-weight:700}
.v2-qpt sup,.v2-qpt sub{line-height:0}
.v2-opt:hover{border-color:${C.primary}}
.v2-expl p{margin:0 0 8px}
.v2-expl ul,.v2-expl ol{margin:4px 0 8px;padding-left:22px}
.v2-ref:hover{background:${C.tint}}
`;

export const card = {
  boxSizing: 'border-box', background: C.surface, borderRadius: 12, padding: 24,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
};

export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// ------------------------------------------------------------------ question id

// "Q 12345" (= questions.id) on every question Harsh sees (C11.1): small grey text he can
// select and copy, so he can note it and ask about it. `style` places it (top-right).
export function QId({ id, style }) {
  if (id === null || id === undefined || id === '') return null;
  return (
    <span data-testid="v2-qid" title="Question ID" style={{
      fontSize: 12, color: '#64748B', fontWeight: 500, whiteSpace: 'nowrap',
      userSelect: 'text', WebkitUserSelect: 'text', cursor: 'text', ...style,
    }}>Q {id}</span>
  );
}

// ------------------------------------------------------------------ PYQ badge

// "NEET 2019 · AIPMT 2012" on a question that came in a past paper (C11.4). `pyq` is the
// payload's {tags, label} or null (then nothing is shown). Fine before answering.
export function PyqBadge({ pyq, style }) {
  if (!pyq || !pyq.label) return null;
  return (
    <span data-testid="v2-pyq" title="This question came in these exams" style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
      padding: '4px 10px', borderRadius: 999, background: C.streakBg, color: C.streak,
      border: '1px solid #F1D3A1', fontFamily: FONT.ui, fontSize: 13, fontWeight: 600,
      lineHeight: 1.3, ...style,
    }}>{pyq.label}</span>
  );
}

// ------------------------------------------------------------------ question body

export function QuestionBody({ q, size = 16 }) {
  return (
    <>
      {q.passage_text && (
        <div data-testid="v2-q-passage" style={{
          background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10,
          padding: '14px 16px', fontSize: 14, lineHeight: 1.9,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 8 }}>Passage</div>
          <QHtml html={q.passage_text} />
        </div>
      )}
      <QHtml html={q.question_text} style={{ fontSize: size, lineHeight: 1.8, fontWeight: 500 }} />
      {q.question_image_url && (
        <img src={q.question_image_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, border: `1px solid ${C.border}` }} />
      )}
    </>
  );
}

// ------------------------------------------------------------------ one option row

// state: 'plain' | 'chosen' | 'wrong' (your answer, wrong) | 'right' (your answer, right)
//        | 'correct' (the correct answer, not yours)
const OPT_LOOK = {
  plain: { border: C.border, bg: C.surface, badge: '#F1F5F9', badgeText: C.muted },
  chosen: { border: C.primary, bg: C.tint, badge: C.primary, badgeText: '#FFFFFF' },
  wrong: { border: C.bad, bg: C.badBg, badge: C.bad, badgeText: '#FFFFFF' },
  right: { border: C.good, bg: C.goodBg, badge: C.good, badgeText: '#FFFFFF' },
  correct: { border: C.good, bg: C.goodBg, badge: C.good, badgeText: '#FFFFFF' },
};

function OptionLabel({ state }) {
  if (state === 'wrong') {
    return (
      <span data-testid="v2-opt-label" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#991B1B' }}>
        <IconWrong />Your answer · Wrong
      </span>
    );
  }
  if (state === 'right') {
    return (
      <span data-testid="v2-opt-label" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#065F46' }}>
        <IconRight />Your answer · Right
      </span>
    );
  }
  if (state === 'correct') {
    return (
      <span data-testid="v2-opt-label" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#065F46' }}>
        <IconRight />Correct answer
      </span>
    );
  }
  if (state === 'chosen') {
    return <span data-testid="v2-opt-label" style={{ fontSize: 13, fontWeight: 600, color: C.primaryDark }}>Your answer</span>;
  }
  return null;
}

export function OptionRow({ letter, html, imageUrl, state = 'plain', onClick, disabled, small = false }) {
  const look = OPT_LOOK[state] || OPT_LOOK.plain;
  const clickable = !!onClick && !disabled;
  const Tag = clickable ? 'button' : 'div';
  return (
    <Tag type={clickable ? 'button' : undefined} onClick={clickable ? onClick : undefined}
      className={clickable ? 'v2-opt' : undefined}
      data-testid={`v2-opt-${letter}`} data-state={state}
      style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, width: '100%',
        boxSizing: 'border-box', padding: small ? '10px 14px' : '14px 16px', borderRadius: 10,
        border: `2px solid ${look.border}`, background: look.bg, textAlign: 'left',
        fontFamily: FONT.ui, color: C.text, cursor: clickable ? 'pointer' : 'default',
        minHeight: 44,
      }}>
      <span style={{
        width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: look.badge,
        color: look.badgeText, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 13,
      }}>{letter}</span>
      <span style={{ flex: '1 1 160px', minWidth: 0, fontSize: small ? 14 : 15 }}>
        <QHtml tag="span" html={html} />
        {imageUrl && <img src={imageUrl} alt="" style={{ display: 'block', maxWidth: '100%', marginTop: 6, borderRadius: 6 }} />}
      </span>
      <OptionLabel state={state} />
    </Tag>
  );
}

// Option state once the answer is known (chapter mode after answering, or the result).
export function revealedState(letter, yourAnswer, correctOption) {
  if (letter === yourAnswer) return letter === correctOption ? 'right' : 'wrong';
  if (letter === correctOption) return 'correct';
  return 'plain';
}

// ------------------------------------------------------------------ notes link + box

// Where "Open this place in notes" / "See this in notes" go (C7.5): the Notes-with-Back view
// /app/test/:testId/notes?at=<point or unit code>&q=<question number>[&from=result].
// Without a test (ctx) the plain notes deep link.
export function notesHref(target, ctx) {
  if (!target || !target.chapter_id) return null;
  const at = target.point_code || target.section_code;
  if (ctx && ctx.testId && ctx.number) {
    const qs = new URLSearchParams();
    if (at) qs.set('at', at);
    qs.set('q', String(ctx.number));
    if (ctx.from === 'result') qs.set('from', 'result');
    return `/app/test/${ctx.testId}/notes?${qs.toString()}`;
  }
  return `/app/notes/${target.chapter_id}${at ? `?at=${encodeURIComponent(at)}` : ''}`;
}

// Where the runner remembers the question he was on (C7.4), also written by the
// Notes-with-Back view before going back (C7.5).
export const testPosKey = (id) => `v2_test_pos_${id}`;
// The result card to scroll back to after "See this in notes" (C7.5).
export const resultPosKey = (id) => `v2_result_pos_${id}`;

export function unitLine(target) {
  if (!target) return '';
  return [target.unit_number, target.unit_title].filter(Boolean).join(' ');
}

// hideLink (Revision, C8.1): only the point; the "See this in notes" button sits below.
export function NotesBox({ target, onOpen, testId, number, hideLink = false }) {
  const href = notesHref(target, { testId, number });
  if (!href) return null;
  const place = unitLine(target);
  return (
    <div data-testid="v2-notes-box" style={{
      boxSizing: 'border-box', padding: 16, border: '1px solid #C7D2FE', borderRadius: 10,
      background: C.tint, display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <IconBook stroke={C.primaryDark} />
        <div style={{ flexGrow: 1, fontFamily: FONT.heading, fontSize: 14, fontWeight: 600, color: C.primaryDark }}>Read this in your notes</div>
        {place && <div data-testid="v2-notes-place" style={{ fontSize: 12, color: C.muted }}>{place}</div>}
      </div>
      {target.point_html ? (
        <div className="v2-qpt" data-testid="v2-notes-point" style={{
          padding: '10px 12px', borderRadius: 8, background: C.surface, fontSize: 15, lineHeight: 1.7,
          fontFamily: FONT.notes, overflowX: 'auto',
        }} dangerouslySetInnerHTML={{ __html: target.point_html }} />
      ) : place ? (
        <div data-testid="v2-notes-point" style={{ padding: '10px 12px', borderRadius: 8, background: C.surface, fontSize: 15 }}>
          Read the part “{place}”.
        </div>
      ) : null}
      {!hideLink && <Link to={href} state={{ v2back: true }} onClick={onOpen} data-testid="v2-open-notes" style={{
        alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8, minHeight: 44,
        padding: '0 16px', borderRadius: 8, background: C.surface, border: `1px solid ${C.primary}`,
        color: C.primaryDark, fontSize: 14, fontWeight: 600, textDecoration: 'none', boxSizing: 'border-box',
      }}>Open this place in notes<IconArrowRight /></Link>}
      {!hideLink && <div style={{ fontSize: 12, color: C.muted }}>Your test will wait. A Back button in the notes brings you here again.</div>}
    </div>
  );
}

// ------------------------------------------------------------------ answer + explanation + notes

// "5.4.1 The Lac operon" for one notes reference (C11.2).
export function refLabel(ref) {
  if (!ref) return '';
  return [ref.number, ref.title].filter(Boolean).join(' ') || ref.code;
}

// One notes reference → the Notes-with-Back view of a test (C7.5) at that place.
// ctx = { testId, number, from? }. The chapter goes in the URL (ch) because a reference
// may be another unit than the question's own notes place.
export function refHref(ref, ctx) {
  if (!ref || !ref.chapter_id || !ctx || !ctx.testId || !ctx.number) return null;
  const qs = new URLSearchParams();
  qs.set('at', ref.point_code || ref.code);
  qs.set('q', String(ctx.number));
  qs.set('ch', String(ref.chapter_id));
  if (ctx.from === 'result') qs.set('from', 'result');
  return `/app/test/${ctx.testId}/notes?${qs.toString()}`;
}

function PanelHead({ children }) {
  return <div style={{ fontFamily: FONT.heading, fontSize: 14, fontWeight: 600, color: C.primaryDark }}>{children}</div>;
}

// What every answered question shows (C11.2, SPEC §10): (1) the correct answer, (2) the
// explanation ("Explanation coming soon" when there is none yet), (3) "Notes reference" =
// every linked notes place as a button that opens it with a Back button ("No notes
// reference yet" when there is none). The answer and explanation always show.
// hrefFor(ref) gives each button's address; pointHtml = the exact notes line, if known;
// waitNote = the grey line under the buttons.
export function AnswerPanel({ correctOption, explanation, imageUrl, refs, hrefFor, onOpenRef, pointHtml, waitNote, size = 15 }) {
  const list = (refs || []).map((r) => ({ ref: r, href: hrefFor ? hrefFor(r) : null })).filter((x) => x.href);
  return (
    <div data-testid="v2-answer-panel" style={{
      boxSizing: 'border-box', padding: 16, border: '1px solid #C7D2FE', borderRadius: 10,
      background: '#F8FAFF', display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div data-testid="v2-answer-correct" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 600, color: C.good }}>
        <IconRight stroke={C.good} />Correct answer: {correctOption || '–'}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <PanelHead>Explanation</PanelHead>
        {explanation ? (
          <MathHtml className="v2-qpt v2-expl" html={explanation} style={{
            fontSize: size, lineHeight: 1.7, color: C.text, overflowX: 'auto',
          }} />
        ) : (
          <div data-testid="v2-expl-soon" style={{ fontSize: 14, color: '#64748B' }}>Explanation coming soon</div>
        )}
        {imageUrl && <img src={imageUrl} alt="" style={{ maxWidth: '100%', borderRadius: 8, border: `1px solid ${C.border}` }} />}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconBook stroke={C.primaryDark} />
          <PanelHead>Notes reference</PanelHead>
        </div>
        {pointHtml && (
          <div className="v2-qpt" data-testid="v2-notes-point" style={{
            padding: '10px 12px', borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`,
            fontSize: 15, lineHeight: 1.7, fontFamily: FONT.notes, overflowX: 'auto',
          }} dangerouslySetInnerHTML={{ __html: pointHtml }} />
        )}
        {list.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {list.map(({ ref, href }) => (
              <Link key={ref.code} to={href} state={{ v2back: true }} className="v2-ref" data-testid="v2-notes-ref"
                onClick={() => onOpenRef && onOpenRef(ref)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 14px',
                  borderRadius: 8, background: C.surface, border: `1px solid ${C.primary}`, color: C.primaryDark,
                  fontSize: 14, fontWeight: 600, textDecoration: 'none', boxSizing: 'border-box',
                }}>{refLabel(ref)}<IconOpen /></Link>
            ))}
          </div>
        ) : (
          <div data-testid="v2-no-ref" style={{ fontSize: 14, color: '#64748B' }}>No notes reference yet</div>
        )}
        {list.length > 0 && waitNote && <div style={{ fontSize: 12, color: C.muted }}>{waitNote}</div>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ open test banner

export function OpenTestBanner({ test, note }) {
  if (!test) return null;
  const left = Math.max(0, (test.total_questions || 0) - (test.answered || 0));
  return (
    <div data-testid="v2-open-test" style={{
      boxSizing: 'border-box', padding: '14px 18px', borderRadius: 12, background: C.tint,
      border: '1px solid #C7D2FE', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
    }}>
      <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ fontFamily: FONT.heading, fontSize: 15, fontWeight: 600, color: C.primaryDark }}>
          {test.mode === 'v2_chapter' ? 'Continue your Chapter Test' : 'Your test is waiting for you'}
        </div>
        <div style={{ fontSize: 14, color: C.muted }}>
          {test.title} · {test.answered} answered · {left} remaining
        </div>
        {note && <div style={{ fontSize: 13, color: C.muted }}>{note}</div>}
      </div>
      <Link to={`/app/test/${test.test_id}`} data-testid="v2-resume-test" style={{
        ...button.primary, display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', boxSizing: 'border-box',
      }}>Continue the test<IconArrowRight /></Link>
    </div>
  );
}

// ------------------------------------------------------------------ confirm dialog

export function ConfirmDialog({ title, text, yes, no, onYes, onNo, busy = false, testId = 'v2-confirm' }) {
  return (
    <div role="dialog" aria-modal="true" data-testid={testId} style={{
      position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(15,23,42,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={(e) => { if (e.target === e.currentTarget && !busy) onNo(); }}>
      <div style={{ ...card, maxWidth: 420, width: '100%', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontFamily: FONT.heading, fontSize: 18, fontWeight: 600 }}>{title}</div>
        {text && <div style={{ fontSize: 15, color: C.muted }}>{text}</div>}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onNo} disabled={busy} data-testid={`${testId}-no`} style={button.secondary}>{no}</button>
          <button type="button" onClick={onYes} disabled={busy} data-testid={`${testId}-yes`} style={button.primary}>{busy ? 'Please wait…' : yes}</button>
        </div>
      </div>
    </div>
  );
}

// "mm:ss", or "h:mm:ss" from one hour on.
export function clock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const two = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${two(m)}:${two(r)}` : `${two(m)}:${two(r)}`;
}

export function errorText(err, fallback = 'Something went wrong. Please try again.') {
  // no answer from the server at all → the internet (C10.1)
  if (err && !err.response) return 'Could not connect. Check the internet and press Try again.';
  const d = err && err.response && err.response.data && err.response.data.detail;
  return typeof d === 'string' ? d : fallback;
}
